/**
 * How much of a position can be closed.
 *
 * A close is not like an open: the exchange gave the trader this position, so
 * the whole of it is always closable — even when it is smaller than the
 * market's minimum order, and even when it does not sit on the market's step.
 * Flooring a full close would strand dust that can then never be closed, and
 * rounding one UP would try to close more than is held.
 *
 * So 100% is the position's own amount, byte for byte, and every smaller stop
 * floors onto the step. Ported from `closeSizeForPercent` / `resolvePerpCloseSize`
 * with the float round trips (and their 1e-9 epsilon) removed.
 */
import { Dec, dec } from "../decimal";
import { fractionOfMax } from "../percent";
import { MarketSteps } from "../spec";
import { MaxResult, maxOk, maxUnavailable } from "./types";

export interface ClosePositionMaxInput {
  /** The position's amount as the API sends it; a short's is negative. */
  positionAmount?: Dec | number | null;
  /** The market's grid, for the percentage stops below 100%. */
  steps: MarketSteps;
  /** Shown beside the maximum: the base asset's symbol. */
  unit?: string;
}

/** The position's size with its sign stripped, or `null` when there is none. */
export const heldAmount = (
  positionAmount?: Dec | number | null
): Dec | null => {
  const parsed = dec.parse(positionAmount ?? null);
  if (parsed === null || dec.isZero(parsed)) return null;
  const held = dec.isNeg(parsed) ? dec.sub("0", parsed) : parsed;
  return dec.gt(held, "0") ? held : null;
};

/**
 * The most that can be closed: the position itself. Never floored, never capped
 * by the market's minimum — see the note above.
 */
export const closePositionMax = (input: ClosePositionMaxInput): MaxResult => {
  const held = heldAmount(input.positionAmount);
  if (held === null) return maxUnavailable("no_position");
  return maxOk(held, "position", input.unit ?? "");
};

/**
 * The size behind a 25/50/75/100% chip.
 *
 * 100% is the position exactly; anything else is floored onto `amountStep`.
 * Returns **"0"** when that leaves nothing to send — a percentage below one
 * step, or no position at all. "0" is the documented "nothing closable here":
 * the caller decides whether to empty the field, disable the chip or show the
 * minimum, because only the caller knows which control asked.
 */
export const closeSizeForPercent = (
  positionAmount: Dec | number | null | undefined,
  pct: number,
  steps: MarketSteps
): Dec => {
  const held = heldAmount(positionAmount);
  if (held === null) return "0";
  return fractionOfMax(held, pct, steps.amountStep);
};

/**
 * The close size to submit, or `null` when the field does not hold one.
 *
 * A size at or above the position is a full close and returns the position's
 * own amount; anything smaller is floored onto the step. `null` means "do not
 * send this" — sending 0 for it would close everything.
 *
 * The float version allowed a 1e-9 slack before calling something a full close,
 * only because a round trip through a double could land a wei either side of
 * the held amount. With exact decimals the comparison is exact.
 */
export const resolveCloseSize = (
  requested: Dec | number | null | undefined,
  positionAmount: Dec | number | null | undefined,
  steps: MarketSteps
): Dec | null => {
  const size = dec.parse(requested ?? null);
  if (size === null || !dec.gt(size, "0")) return null;

  const held = heldAmount(positionAmount);
  if (held !== null && dec.gte(size, held)) return held;

  const floored = dec.toStep(size, steps.amountStep, "floor") ?? size;
  return dec.gt(floored, "0") ? floored : null;
};
